import { ref, onMounted, onUnmounted, computed } from "vue";

/**
 * Request new speech frequencies at request animation frame interval
 * @param {() => Float32Array} fetcher
 * @returns { { speechFrequencies: import('vue').Ref<Float32Array>, isSpeaking: import('vue').ComputedRef<boolean> } }
 */
export default function useSpeechFrequencies(fetcher) {
    const speechFrequencies = ref(new Float32Array(72));

    const isSpeaking = computed(() => {
        const threshold = 0.2; // Adjust this value based on your specific use case
        const significantFrequencies = speechFrequencies.value.filter(freq => freq > threshold);
        return significantFrequencies.length > 6; // Consider speaking if more than 5 frequencies are above the threshold
    });

    let animationFrameId = null;

    const animate = () => {
        speechFrequencies.value = fetcher();
        animationFrameId = requestAnimationFrame(animate);
    };

    onMounted(() => {
        animate();
    });

    onUnmounted(() => {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
    });

    return { speechFrequencies, isSpeaking };
}


/**
 * Generate dummy random speech frequencies for simulating voice chat
 * @returns {Float32Array}
 */
export function generateSpeechFrequencies(seed = 0) {
    const frequencies = new Float32Array(72);
    const time = Date.now() / 400;
    
    // Use seed to offset time and create different patterns
    const offsetTime = time + (seed * 1000);
    const phaseOffset = seed * Math.PI; // Different phase for each instance
    
    // Create "syllable" effect with different phases for each seed
    const syllableRate = Math.sin(offsetTime * 0.2 + phaseOffset) > 0;
    const intensity = syllableRate ? 0.8 : 0.2;
    
    for (let i = 0; i < frequencies.length; i++) {
        // Create frequency bands with phase differences
        const band1 = (i < 20) ? 1.2 : 0.4;
        const band2 = (i >= 20 && i < 40) ? 1.0 : 0.3;
        const band3 = (i >= 40) ? 0.8 : 0.2;
        
        // Use seed to create different wave patterns
        const wave1 = Math.sin(offsetTime * 2 + i * 0.2 + phaseOffset) * 0.5;
        const wave2 = Math.cos(offsetTime * 3 + i * 0.1 + phaseOffset * 2) * 0.4;
        const random = Math.random() * 0.2 * (seed + 1);
        
        frequencies[i] = Math.max(0, Math.min(0.5,
            intensity * (band1 + band2 + band3) * (0.3 + wave1 + wave2 + random)
        ));
    }
    
    return frequencies;
}
